import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';

/**
 * Fixtures for the "mid-journey borrower" PSLF demo: ~mid-way through the
 * 120 qualifying payments, three federal loans (NSLDS-linked), one submitted
 * PSLF form, and an active recommendation. Drives the main-app PSLF dashboard
 * (routes/authenticated/pslf{,/dashboard}) entirely through the demo handler.
 *
 * IDs: employee/person ids are placeholders — the demo handler rewrites them
 * (and any sideloaded back-references) to the live session ids the route asks
 * for. company / pslf-profile / pslf-form / etc. use stable demo ids that the
 * fixtures cross-reference, so the materialized records form one graph.
 *
 * Borrower/employer names derive from the {@link DemoFixtureIdentity} the
 * registry resolves from the active selection — the handler's id-rewrite means
 * these resources can overwrite the session identity records, so hardcoded
 * names here would rename the demo user/employer mid-session.
 */

const FEIN = '12-3456789';

const demoEmail = (identity: DemoFixtureIdentity) =>
  `${identity.firstName}.${identity.lastName}`.toLowerCase().replace(/\s+/g, '-') + '@example.com';

// (A) getLoanSummaryRequest builder — plain LoanSummary object (not JSON:API),
// returned as `.content`. Balances are dollars.
const LOAN_SUMMARY = {
  summaryByLoanType: [
    {
      loanType: 'direct',
      loanIds: ['demo-loan-1', 'demo-loan-2'],
      currentBalance: 41860,
      interestRate: 5.47,
      monthlyPayment: 453.67,
      repaymentPlan: 'ibr',
      repaymentPlans: ['ibr'],
      totalDisbursedAmount: 47720,
      federalLoanTime: '120',
    },
  ],
  errors: [],
};

// (B) employee findRecord — sideloads pslf-profile (so no createRecord fires),
// company (route reads employee.company.id), employee-addition, pslf-forms.
function buildEmployeeDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { firstName, lastName, employerName } = identity;
  return {
    data: {
      type: 'employee',
      id: 'demo-employee-1',
      attributes: {
        firstName,
        lastName,
        email: demoEmail(identity),
        isActive: true,
        title: 'Registered Nurse',
        employmentStartDate: '2022-01-15',
        customAttributes: { fein: FEIN },
      },
      relationships: {
        pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        company: { data: { type: 'company', id: 'demo-company-1' } },
        employeeAddition: { data: { type: 'employee-addition', id: 'demo-emp-add-1' } },
        pslfForms: { data: [{ type: 'pslf-form', id: 'demo-form-1' }] },
      },
    },
    included: [
      {
        type: 'pslf-profile',
        id: 'demo-pslf-profile-1',
        attributes: { isInterested: true, signaturePresent: true },
        relationships: {
          employee: { data: { type: 'employee', id: 'demo-employee-1' } },
          pslfForms: { data: [{ type: 'pslf-form', id: 'demo-form-1' }] },
        },
      },
      {
        type: 'company',
        id: 'demo-company-1',
        attributes: {
          name: employerName,
          companyType: 'partner',
          isActive: true,
          programTitle: 'PSLF Program',
        },
        relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
      },
      {
        type: 'employee-addition',
        id: 'demo-emp-add-1',
        attributes: { processed: { merged: { fein: FEIN } } },
      },
      {
        type: 'pslf-form',
        id: 'demo-form-1',
        attributes: { status: 'submitted', formType: 'currentEmployer' },
        relationships: {
          pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
        },
      },
    ],
  };
}

// (C) company findRecord — sideloads pslf-groups (route returns company.pslfGroups).
function buildCompanyDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { employerName } = identity;
  return {
    data: {
      type: 'company',
      id: 'demo-company-1',
      attributes: {
        name: employerName,
        companyType: 'partner',
        isActive: true,
        programTitle: 'PSLF Program',
      },
      relationships: { pslfGroups: { data: [{ type: 'pslf-group', id: 'demo-pslf-group-1' }] } },
    },
    included: [
      {
        type: 'pslf-group',
        id: 'demo-pslf-group-1',
        attributes: {
          approverEmail: 'hr@example.com',
          approverName: 'Sarah Johnson',
          approverTitle: 'HR Director',
          employerCommonName: employerName,
          employerLegalName: `${employerName} Inc.`,
          employerFein: FEIN,
          employerIsNotForProfit: true,
          employerCity: 'Portland',
          employerState: 'OR',
          employerPostalCode: '97205',
        },
        relationships: { company: { data: { type: 'company', id: 'demo-company-1' } } },
      },
    ],
  };
}

// (D) person findRecord — sideloads accounts -> loans + financial-institution.
// NSLDS provider + per-loan forgiveness-program-qualified drives hasNsldsPslfData.
function buildPersonDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const { firstName, lastName } = identity;
  return {
    data: {
      type: 'person',
      id: 'demo-person-1',
      attributes: { firstName, lastName, preferences: {} },
      relationships: { accounts: { data: [{ type: 'account', id: 'demo-account-1' }] } },
    },
    included: [
      {
        type: 'account',
        id: 'demo-account-1',
        attributes: {
          isActive: true,
          publicOrdinalValue: 1,
          observation: {
            provider: 'ObservableProvider.NSLDS',
            balance: 41860,
            'interest-weighted-average': 5.47,
            'principal-balance': 41860,
            'interest-balance': 0,
            'is-paid-off': false,
          },
        },
        relationships: {
          person: { data: { type: 'person', id: 'demo-person-1' } },
          financialInstitution: { data: { type: 'financial-institution', id: 'demo-fi-1' } },
          loans: {
            data: [
              { type: 'loan', id: 'demo-loan-1' },
              { type: 'loan', id: 'demo-loan-2' },
            ],
          },
        },
      },
      {
        type: 'financial-institution',
        id: 'demo-fi-1',
        attributes: { name: 'MOHELA', isActive: true, isPublic: true },
      },
      {
        type: 'loan',
        id: 'demo-loan-1',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': 'Direct Unsubsidized',
            'loan-type': 'direct',
            'raw-loan-type': 'Direct Unsubsidized',
            balance: 28410,
            'principal-balance': 28410,
            'interest-rate': 5.87,
            'repayment-plan-derived': 'ibr',
            'forgiveness-program-qualified': 51,
            'is-paid-off': false,
            status: 'active',
            'status-derived': 'LoanStatus.REPAYMENT',
          },
        },
        relationships: { account: { data: { type: 'account', id: 'demo-account-1' } } },
      },
      {
        type: 'loan',
        id: 'demo-loan-2',
        attributes: {
          isActive: true,
          observation: {
            'loan-name': 'Direct Subsidized',
            'loan-type': 'direct',
            'raw-loan-type': 'Direct Subsidized',
            balance: 13450,
            'principal-balance': 13450,
            'interest-rate': 4.63,
            'repayment-plan-derived': 'ibr',
            'forgiveness-program-qualified': 43,
            'is-paid-off': false,
            status: 'active',
            'status-derived': 'LoanStatus.REPAYMENT',
          },
        },
        relationships: { account: { data: { type: 'account', id: 'demo-account-1' } } },
      },
    ],
  };
}

// Inline PDF (data URI) for the demo form's attachment — a real, minimal PDF so
// the form/success route's preview renders client-side instead of fetching a
// signed-S3 url the network-free block would stop.
const DEMO_PSLF_FORM_PDF =
  'data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2JqCjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iagozIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgMzAwIDIwMF0vUmVzb3VyY2VzPDwvRm9udDw8L0YxIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+ZW5kb2JqCjQgMCBvYmo8PC9UeXBlL0ZvbnQvU3VidHlwZS9UeXBlMS9CYXNlRm9udC9IZWx2ZXRpY2E+PmVuZG9iago1IDAgb2JqPDwvTGVuZ3RoIDYyPj5zdHJlYW0KQlQgL0YxIDE0IFRmIDQwIDEyMCBUZCAoUFNMRiBGb3JtIC0gRGVtbyBEb2N1bWVudCkgVGogRVQKZW5kc3RyZWFtIGVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCnRyYWlsZXI8PC9Sb290IDEgMCBSL1NpemUgNj4+CnN0YXJ0eHJlZgowCiUlRU9G';

const PSLF_FORM_ATTACHMENT: JsonApiResource = {
  type: 'attachment',
  id: 'demo-pslf-attachment-1',
  attributes: {
    filename: 'pslf-form-demo.pdf',
    contentType: 'application/pdf',
    signedUrl: DEMO_PSLF_FORM_PDF,
  },
};

// (E) pslf-form — one submitted current-employer form so the dashboard renders
// the rich (forms) state; the SAME form + inline-PDF attachment is served for
// the form/success single read.
function buildPslfFormResource(id: string, identity: DemoFixtureIdentity): JsonApiResource {
  const { firstName, lastName, employerName } = identity;
  return {
    type: 'pslf-form',
    id,
    attributes: {
      status: 'submitted',
      formType: 'currentEmployer',
      createdAt: '2026-05-16T09:00:00.000Z',
      borrowerCompletedAt: '2026-05-17',
      employerCompletedAt: '2026-05-21',
      submittedAt: '2026-05-22',
      isNonPartner: false,
      borrowerFields: {
        'borrower-name': `${firstName} ${lastName}`,
        'borrower-email': demoEmail(identity),
        'borrower-employer-name': employerName,
        'borrower-employment-start-date': '2022-01-15',
        'borrower-employment-status': 'employed',
        'borrower-employment-continues': true,
        'borrower-employer-ein': FEIN,
      },
      employerFields: {
        'employer-name': employerName,
        'employer-fein': FEIN,
        'employer-city': 'Portland',
        'employer-state': 'OR',
        'employer-zip': '97205',
        'employer-email': 'hr@example.com',
      },
    },
    relationships: {
      pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
      attachments: { data: [{ type: 'attachment', id: 'demo-pslf-attachment-1' }] },
    },
  };
}

function buildPslfFormCollection(identity: DemoFixtureIdentity): JsonApiDoc {
  return {
    data: [buildPslfFormResource('demo-form-1', identity)],
    included: [PSLF_FORM_ATTACHMENT],
    meta: { page: { total: 1 } },
  };
}

// The form/success route reads a SINGLE pslf-form by id (findRecord, include:
// attachments). The id comes from a dashboard link or the just-submitted form's
// client id, so echo it — mirroring the collection form + its attachment.
function buildPslfFormSingle(url: string, identity: DemoFixtureIdentity): JsonApiDoc {
  const id = /\/pslf-forms\/([^/?]+)/.exec(url)?.[1] ?? 'demo-form-1';
  return {
    data: buildPslfFormResource(id, identity),
    included: [PSLF_FORM_ATTACHMENT],
  };
}

// (F) recommendation query — drives the recommendation card + the dashboard
// history table. Plan summaries are arrays of cents (model getters divide by
// 100). User relationship omitted (the dashboard does not dereference it) to
// avoid coupling to the live user id. `createdAt` is load-bearing: the dashboard
// sorts the combined history newest-first on it.
const RECOMMENDATION_ATTRS = {
  hasErrorInCalcs: false,
  bestPlanSummary: {
    // Reconciles on screen: payoff = monthly x term, and
    // forgiven = balance + interest accrued - amount actually paid.
    'payoff-amount': [2472007],
    'term-months': [71],
    'monthly-payments': [34817],
    'total-interest-paid': [1354764],
    'estimated-amount-forgiven': [3068757],
  },
  currentPlanSummary: {
    // Standard 10-year on the $41,860 balance at 5.47%: payoff = monthly x
    // 120 AND = principal + interest, since nothing is forgiven.
    'payoff-amount': [5444040],
    'term-months': [120],
    'monthly-payments': [45367],
    'total-interest-paid': [1258040],
    'estimated-amount-forgiven': [0],
  },
  bestPlanNextSteps: [],
  bestPlanTableHeadings: [],
  currentPlanTableHeadings: [],
  inputSummary: {},
  outputSummary: {},
};

// The modern (PSLF-0726) recommendation — the estimator's product; newest in the
// history table and the source the dashboard's estimate surfaces prefer.
const RECOMMENDATION_0726_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'recommendation',
      id: 'demo-recommendation-0726-1',
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF-0726',
        createdAt: '2026-06-15T14:00:00.000Z',
      },
    },
  ],
  meta: { page: { total: 1 } },
};

// The legacy PSLF recommendation — powers the hero "View Estimate"
// (`mostRecentRecommendation = legacyPslfRecs[0]`) and a second history row.
const RECOMMENDATION_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'recommendation',
      id: 'demo-recommendation-1',
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF',
        createdAt: '2026-05-20T14:00:00.000Z',
      },
    },
  ],
  meta: { page: { total: 1 } },
};

// (G) answer-profile query — returned non-empty so the dashboard never triggers
// createRecord. Null *At fields keep the income/repayment recommendations live.
const ANSWER_PROFILE_COLLECTION: JsonApiDoc = {
  data: [
    {
      type: 'answer-profile',
      id: 'demo-answer-profile-1',
      attributes: {
        consolidatedLoansAt: null,
        changedRepaymentPlanAt: null,
        recertifiedIncomeAt: null,
      },
    },
  ],
};

// (H) observable-documents query — a shared "My Documents" widget on the
// authenticated shell queries this. A demo PSLF borrower has no scanned uploads
// (their PSLF PDFs live on the forms table), so a well-formed EMPTY page renders
// the clean empty state. `meta.page.total` is required — consumers read it and
// an inert block body (no `page`) crashes the table ("reading 'total'").
const EMPTY_OBSERVABLE_DOCUMENTS: JsonApiDoc = { data: [], meta: { page: { total: 0 } } };

// A single-recommendation read for the estimator RESULTS route
// (`findRecord('recommendation', id)` — reached by clicking a dashboard history
// row). The id comes from the fixture history (outside a collection body), so
// echo it into the returned resource, mirroring the request handler's op-path
// `withEchoedId`.
function buildRecommendationEcho(url: string): JsonApiDoc {
  const id = /\/recommendations\/([^/?]+)/.exec(url)?.[1] ?? 'demo-recommendation-0726-1';
  return {
    data: {
      type: 'recommendation',
      id,
      attributes: {
        ...RECOMMENDATION_ATTRS,
        requestingApp: 'PSLF-0726',
        createdAt: '2026-06-15T14:00:00.000Z',
      },
    },
  };
}

const isRecommendationCollection = (url: string) =>
  url.includes('/recommendations') && url.includes('requesting-app');
// Match the EXACT requesting-app value, not a prefix: 'PSLF' must not swallow
// 'PSLF-0726', and neither may serve another product's recommendation query
// (e.g. IDR) — that would hide that product's fixture gap. `[^=]*` skips the
// encoded/plain `]` between the filter key and `=`.
const requestingAppIs = (url: string, app: string) => {
  // Escape regex metacharacters — the current callers pass safe constants, but
  // keep the generic helper robust if an app id ever contains them.
  const escaped = app.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`requesting-app[^=]*=${escaped}(?:&|$)`).test(url);
};
const isSingleResource = (segment: string) => (url: string) =>
  new RegExp(`/${segment}/[^/?]+(?:\\?|$)`).test(url);
const isRecommendationSingle = isSingleResource('recommendations');
const isPslfFormSingle = isSingleResource('pslf-forms');

/**
 * Seam-B (fetch interceptor) matchers for the SHIPPED PSLF dashboard. Unlike the
 * reimagined dashboard (legacy store ops → the request HANDLER / op-matchers in
 * {@link buildMidJourneyPslfMatchers}), the shipped route
 * (routes/authenticated/pslf/dashboard/index.ts) issues its product reads as
 * WarpDrive builder requests that carry no `SkipCache`, so the demo handler
 * defers them and they reach the fetch seam as plain `GET`s. Without these the
 * demo-coverage audit reports them as network-free "blocked" gaps (empty
 * recommendation card, forms table, loan summary). The fixture BODIES are shared
 * with the op-matchers so the two seams never drift.
 *
 * Bodies are raw JSON:API/`content` payloads (camelCase — the app negotiates
 * `x-json-api-key-format: camelized`), exactly as the live wire would return.
 */
export function buildMidJourneyPslfFetchMatchers(identity: DemoFixtureIdentity): FetchMatcher[] {
  return [
    // getLoanSummaryRequest → GET /users/<id>/summarize-loans (plain LoanSummary).
    { urlIncludes: 'summarize-loans', method: 'GET', json: LOAN_SUMMARY },
    // recommendation queries — matched on the EXACT requesting-app so the two
    // dashboard queries get distinct rows (PSLF-0726 + legacy PSLF) and other
    // products' recommendation queries (e.g. IDR) fall through to the block
    // instead of being served PSLF data.
    {
      urlMatches: (url) => isRecommendationCollection(url) && requestingAppIs(url, 'PSLF-0726'),
      method: 'GET',
      json: RECOMMENDATION_0726_COLLECTION,
    },
    {
      urlMatches: (url) => isRecommendationCollection(url) && requestingAppIs(url, 'PSLF'),
      method: 'GET',
      json: RECOMMENDATION_COLLECTION,
    },
    // Single recommendation (results route).
    { urlMatches: isRecommendationSingle, method: 'GET', json: buildRecommendationEcho },
    // pslf-form: the single read (form/success route, findRecord) is matched
    // FIRST so it gets a single-resource body; the collection query (dashboard
    // filter[pslf-profile]; drawer filter[employee-id]) is the fallback.
    {
      urlMatches: isPslfFormSingle,
      method: 'GET',
      json: (url: string) => buildPslfFormSingle(url, identity),
    },
    {
      urlMatches: (url) => url.includes('/pslf-forms') && !isPslfFormSingle(url),
      method: 'GET',
      json: buildPslfFormCollection(identity),
    },
    // Shared "My Documents" widget — coherent empty page.
    { urlIncludes: '/observable-documents', method: 'GET', json: EMPTY_OBSERVABLE_DOCUMENTS },
    // answer-profile findOrCreate (dash-recommendations, on render): a non-empty
    // query result short-circuits the createRecord (mutations are blocked in demo).
    { urlIncludes: '/answer-profiles', method: 'GET', json: ANSWER_PROFILE_COLLECTION },
  ];
}

export function buildMidJourneyPslfMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    { matcher: { kind: 'builder', urlIncludes: 'summarize-loans' }, content: LOAN_SUMMARY },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'employee' },
      docs: [buildEmployeeDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'company' },
      docs: [buildCompanyDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'person' },
      docs: [buildPersonDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'pslf-form' },
      docs: [buildPslfFormCollection(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'recommendation' },
      docs: [RECOMMENDATION_COLLECTION],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'answer-profile' },
      docs: [ANSWER_PROFILE_COLLECTION],
    },
  ];
}
